import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import {
  type Destination,
  useDeleteAllTagAssignments,
  useDeleteTagAssignment,
  useDestinationsTypeahead,
  usePlaces,
  useTagAssignmentsForForm,
} from '../../hooks';
import {
  DATAGRID_BORDERED_CONTAINER_SX,
  DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
  DATAGRID_EXPAND_TO_ROWS_SX,
  DATAGRID_HIDE_SEPARATORS_SX,
  DELETE_ALL_HEADER_BUTTON_SX,
} from '../../styles';

interface Props {
  tagId?: string;
  selectedPlaceIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
}

export default function TagAssignmentsDestination({ tagId, selectedPlaceIds, onSelectionChange }: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [destinationSearchQuery, setDestinationSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const { paginationModel: savedPaginationModel, setPaginationModel: setSavedPaginationModel } = useDataGridPagination({
    pageSize: 3,
    page: 0,
  });

  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const isLoadingAssignments = tagAssignmentsData.isLoading;

  const deleteTagAssignment = useDeleteTagAssignment();
  const deleteAllTagAssignments = useDeleteAllTagAssignments();
  const debouncedDestinationSearchQuery = useMemo(() => debounce((value: string) => setDebouncedQuery(value), 500), []);

  const destinationAssignments = tagAssignmentsData.savedAssignments.filter((a) => a.entityType === 'DESTINATION');
  const savedPlaceIds = useMemo(() => destinationAssignments.map((a) => a.entityId), [destinationAssignments]);
  const draftPlaceIds = useMemo(
    () => selectedPlaceIds.filter((placeId) => !savedPlaceIds.includes(placeId)),
    [savedPlaceIds, selectedPlaceIds],
  );
  const displayedPlaceIds = useMemo(
    () => Array.from(new Set([...savedPlaceIds, ...draftPlaceIds])),
    [draftPlaceIds, savedPlaceIds],
  );

  const placesQueries = usePlaces({
    placeIds: displayedPlaceIds,
    enabled: displayedPlaceIds.length > 0 && !isLoadingAssignments,
  });

  const placeNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    displayedPlaceIds.forEach((placeId, index) => {
      const query = placesQueries[index];

      if (!query) {
        return;
      }

      if (query.data?.result?.result) {
        const place = query.data.result.result;
        const name = place.name || place.nameFull || placeId;
        map.set(placeId, name);
      } else if (placeId) {
        map.set(placeId, placeId);
      }
    });
    return map;
  }, [displayedPlaceIds, placesQueries]);

  const loadingPlaceIds = useMemo(() => {
    const ids = new Set<string>();

    displayedPlaceIds.forEach((placeId, index) => {
      if (placesQueries[index]?.isLoading) {
        ids.add(placeId);
      }
    });

    return ids;
  }, [displayedPlaceIds, placesQueries]);

  const { data: typeaheadData, isLoading: isTypeaheadLoading } = useDestinationsTypeahead({
    query: debouncedQuery,
    enabled: debouncedQuery.length >= 2,
  });

  const destinationSearchResults = useMemo(() => {
    if (!typeaheadData?.result) {
      return [];
    }

    const selectedIds = new Set([...savedPlaceIds, ...selectedPlaceIds]);

    return typeaheadData.result
      .map((result) => ({
        fk: result.id,
        id: result.id,
        primary_text: result.format.mainText,
        secondary_text: result.format.secondaryText ?? '',
        type: result.searchType,
      }))
      .filter((result) => !selectedIds.has(result.fk || result.id || '')) as Array<Destination>;
  }, [typeaheadData, savedPlaceIds, selectedPlaceIds]);

  const destinationRows = useMemo(() => {
    const savedRows = destinationAssignments.map((assignment) => ({
      ...assignment,
      rowId: assignment.id,
      isDraft: false,
    }));

    const draftRows = draftPlaceIds.map((placeId) => ({
      id: `draft-${placeId}`,
      rowId: `draft-${placeId}`,
      entityId: placeId,
      isDraft: true,
    }));

    return [...savedRows, ...draftRows];
  }, [destinationAssignments, draftPlaceIds]);

  const visibleDestinationAssignments = useMemo(() => {
    const start = savedPaginationModel.page * savedPaginationModel.pageSize;
    const end = start + savedPaginationModel.pageSize;
    return destinationRows.slice(start, end);
  }, [destinationRows, savedPaginationModel.page, savedPaginationModel.pageSize]);
  const previousDestinationRowCountRef = useRef(destinationRows.length);
  const pendingDestinationAddTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDeleteClick = (assignmentId: string) => {
    if (savedAssignments.length === 1) {
      enqueueSnackbar('Cannot delete the unique assignment. A tag must have at least one assignment.', {
        variant: 'error',
      });
      return;
    }
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const savedAssignmentsColumns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'entityId',
        headerName: 'Entity ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const isLoadingPlace = loadingPlaceIds.has(params.row.entityId);
          if (isLoadingPlace) {
            return (
              <Box display="flex" alignItems="center" height="100%">
                <CircularProgress size={16} />
              </Box>
            );
          }
          const placeName = placeNamesMap.get(params.row.entityId) || params.row.entityId;
          return (
            <Box display="flex" alignItems="center" height="100%">
              {placeName}
            </Box>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Assignment time',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.isDraft ? '—' : params.row.createdAt ? formatDateSlashesWithClock(params.row.createdAt) : '—'}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => {
          if (displayedPlaceIds.length === 0) {
            return null;
          }

          return (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={handleDeleteAllClick}
              disabled={deleteAllTagAssignments.isPending}
              sx={DELETE_ALL_HEADER_BUTTON_SX}
              endIcon={
                deleteAllTagAssignments.isPending ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
              }
            >
              Delete all
            </Button>
          );
        },
        renderCell: (params) => {
          if (params.row.isDraft) {
            return (
              <IconButton size="small" color="error" onClick={() => handleDestinationRemove(params.row.entityId)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            );
          }

          const isDeleting = deleteTagAssignment.isPending && assignmentToDelete === params.row.id;
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.id)}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deleteAllTagAssignments.isPending,
      deleteTagAssignment.isPending,
      assignmentToDelete,
      displayedPlaceIds.length,
      loadingPlaceIds,
      placeNamesMap,
    ],
  );

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) {
      return;
    }
    await deleteTagAssignment.mutateAsync(assignmentToDelete);
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteAllConfirm = async () => {
    const assignmentIds = destinationAssignments.map((assignment) => assignment.id);

    if (assignmentIds.length > 0) {
      await deleteAllTagAssignments.mutateAsync(assignmentIds);
    }

    onSelectionChange([]);
    setDeleteAllDialogOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const handleDestinationsChange = (newPlaceIds: Array<string>) => {
    if (tagId && savedPlaceIds.length > 0) {
      const combinedPlaceIds = [...savedPlaceIds, ...newPlaceIds.filter((id) => !savedPlaceIds.includes(id))];
      onSelectionChange(combinedPlaceIds);
    } else {
      onSelectionChange(newPlaceIds);
    }
  };

  const handleDestinationRemove = (placeIdToRemove: string) => {
    if (tagId && savedPlaceIds.includes(placeIdToRemove)) {
      return;
    }
    onSelectionChange(selectedPlaceIds.filter((id) => id !== placeIdToRemove));
  };

  const handleDestinationSearchInputChange = (newInputValue: string) => {
    setDestinationSearchQuery(newInputValue);
    debouncedDestinationSearchQuery(newInputValue);
  };

  const handleDestinationDraftAdd = (destination: Destination | null) => {
    if (!destination) {
      return;
    }

    const destinationId = destination.fk || destination.id || '';
    if (!destinationId) {
      return;
    }

    const nextSelectedIds = Array.from(new Set([...selectedPlaceIds, destinationId]));
    const nextDraftCount = nextSelectedIds.filter((placeId) => !savedPlaceIds.includes(placeId)).length;
    const nextRowCount = destinationAssignments.length + nextDraftCount;
    const lastPage = Math.max(0, Math.ceil(nextRowCount / savedPaginationModel.pageSize) - 1);

    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page: lastPage,
    }));

    if (pendingDestinationAddTimeoutRef.current) {
      clearTimeout(pendingDestinationAddTimeoutRef.current);
    }

    pendingDestinationAddTimeoutRef.current = setTimeout(() => {
      handleDestinationsChange(nextSelectedIds);
      setDestinationSearchQuery('');
      setDebouncedQuery('');
      pendingDestinationAddTimeoutRef.current = null;
    }, 500);
  };

  const handleSavedPageChange = (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page,
    }));
  };

  const handleSavedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page: 0,
      pageSize: Number(event.target.value),
    }));
  };

  useEffect(() => {
    return () => debouncedDestinationSearchQuery.cancel();
  }, [debouncedDestinationSearchQuery]);

  useEffect(() => {
    return () => {
      if (pendingDestinationAddTimeoutRef.current) {
        clearTimeout(pendingDestinationAddTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const previousRowCount = previousDestinationRowCountRef.current;
    const nextRowCount = destinationRows.length;

    previousDestinationRowCountRef.current = nextRowCount;

    if (nextRowCount > previousRowCount) {
      const lastPage = Math.max(0, Math.ceil(nextRowCount / savedPaginationModel.pageSize) - 1);
      setSavedPaginationModel((currentModel) => ({
        ...currentModel,
        page: lastPage,
      }));
    }
  }, [destinationRows.length, savedPaginationModel.pageSize, setSavedPaginationModel]);

  return (
    <Box>
      {tagId && isLoadingAssignments ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Box sx={DATAGRID_BORDERED_CONTAINER_SX}>
            <DataGrid
              rows={visibleDestinationAssignments}
              columns={savedAssignmentsColumns}
              getRowId={(row) => row.rowId}
              localeText={{ noRowsLabel: 'None' }}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              sx={[
                { border: 0 },
                DATAGRID_EXPAND_TO_ROWS_SX,
                DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
                DATAGRID_HIDE_SEPARATORS_SX,
              ]}
            />
            {destinationRows.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <TablePagination
                  component="div"
                  count={destinationRows.length}
                  page={savedPaginationModel.page}
                  onPageChange={handleSavedPageChange}
                  rowsPerPage={savedPaginationModel.pageSize}
                  onRowsPerPageChange={handleSavedRowsPerPageChange}
                  rowsPerPageOptions={[3, 10, 25, 50]}
                />
              </Box>
            )}
          </Box>
          <Box>
            <Autocomplete
              options={destinationSearchResults}
              value={null}
              inputValue={destinationSearchQuery}
              onInputChange={(_, newInputValue) => handleDestinationSearchInputChange(newInputValue)}
              onChange={(_, newValue) => handleDestinationDraftAdd(newValue)}
              loading={isTypeaheadLoading}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option.primary_text || ''}
              isOptionEqualToValue={(option, value) => (option.fk || option.id || '') === (value.fk || value.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for destinations to tag"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isTypeaheadLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.fk || option.id || ''}>
                  <Typography>
                    {option.primary_text || ''}
                    {option.secondary_text && (
                      <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                        {option.secondary_text}
                      </Typography>
                    )}
                  </Typography>
                </li>
              )}
              noOptionsText={debouncedQuery.length < 2 ? 'Type at least 2 characters' : 'No destinations found'}
              fullWidth
            />
          </Box>
        </Stack>
      )}
      <Dialog open={deleteDialogOpen} onClose={deleteTagAssignment.isPending ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="text" disabled={deleteTagAssignment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteTagAssignment.isPending}
            startIcon={deleteTagAssignment.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteAllDialogOpen}
        onClose={deleteAllTagAssignments.isPending ? undefined : handleDeleteAllCancel}
      >
        <DialogTitle>Delete All Destination Assignments</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all {displayedPlaceIds.length} destination assignment(s)? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAllCancel} variant="text" disabled={deleteAllTagAssignments.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteAllTagAssignments.isPending}
            startIcon={deleteAllTagAssignments.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
